import { useCallback, useMemo, useRef } from "react";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import type {
  Location,
  ParameterTimeSeriesConfiguration,
  ParametersLocation,
  TimeSeriesWidgetConfiguration,
} from "@/lib/api";
import { useDashboardData } from "@/features/shared/hooks";
import { makeTimeSeriesConfigs } from "@/features/shared/widgets/map/time-series/make-time-series-configs";
import {
  keys as observedQueryKeys,
  useObservedLocationsQuery,
} from "@/features/shared/widgets/observations-map/hooks/queries";
import { useRefreshAction } from "@/features/dashboard/features/refresh-timer/hooks";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { useNow } from "@/features/shared/hooks";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import Graph from "@/components/graph/graph";
import { getSharedChartTimeBounds } from "@/components/graph/get-shared-chart-time-bounds";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import Stack from "@/components/stack/stack";
import { useTranslation } from "@/lib/i18n";

import { findObservedStation, resolveObservedStationId } from "./resolve-observed-station";
import { useAllTimeSeriesParametersQuery } from "./use-all-time-series-parameters-query";
import { useTimeSeriesWidgetChartData } from "./use-time-series-widget-chart-data";
import { useElementHeight } from "./use-element-height";
import styles from "./time-series-widget.module.css";

type TimeSeriesWidgetViewProps = {
  configuration: TimeSeriesWidgetConfiguration;
  selectedLocation: Location | undefined;
};

/**
 * Fill-to-box chart for a `time_series` widget. Takes the dashboard location
 * explicitly so the CMS preview can pass admin locations instead of published
 * ones from {@link useDashboardData}.
 */
export function TimeSeriesWidgetView({
  configuration,
  selectedLocation,
}: TimeSeriesWidgetViewProps) {
  const { t } = useTranslation();
  const parametersQuery = useAllTimeSeriesParametersQuery();
  const locationsQuery = useObservedLocationsQuery();

  const chartConfig = configuration.parameter_time_series_configuration;
  const parameterOptions = useMemo(
    () => parametersQuery.data ?? [],
    [parametersQuery.data],
  );
  const parameterModelIndex = parametersQuery.parameterModelIndex;
  const timeSeriesConfigs = useMemo(
    () =>
      chartConfig
        ? makeTimeSeriesConfigs([chartConfig], parameterOptions, { parameterModelIndex })
        : [],
    [chartConfig, parameterOptions, parameterModelIndex],
  );
  const timeSeriesConfig = useMemo(
    () => (timeSeriesConfigs[0] ? { ...timeSeriesConfigs[0], title: "" } : undefined),
    [timeSeriesConfigs],
  );

  const stationId = resolveObservedStationId(selectedLocation);
  const station = findObservedStation(locationsQuery.data, stationId);
  const latlng = useMemo(() => {
    const lat = selectedLocation?.latitude;
    const lng = selectedLocation?.longitude;
    if (lat != null && lng != null) return { lat, lng };
    return undefined;
  }, [selectedLocation?.latitude, selectedLocation?.longitude]);

  const queryClient = useQueryClient();
  const refresh = useCallback(async () => {
    if (!stationId) return;
    await queryClient.invalidateQueries({
      queryKey: observedQueryKeys.locationParameterSeriesPrefix(stationId),
    });
  }, [queryClient, stationId]);
  useRefreshAction(refresh);

  // Resolve loading first: the parameter catalogue and locations must settle
  // before any "empty" state is real. Otherwise the still-empty `timeSeriesConfigs`
  // (no catalogue yet) or unresolved station would flash an explainer that names
  // just one of several possible causes while data is merely still loading.
  if (parametersQuery.isPending || locationsQuery.isPending) {
    return (
      <div className={styles.root}>
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size={24} color="inherit" />
        </Stack>
      </div>
    );
  }

  if (!chartConfig || !timeSeriesConfig) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>{t("admin.widgets.fields.time_series.empty_config")}</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <TimeSeriesWidgetChart
        location={station}
        latlng={latlng}
        timeSeriesConfig={timeSeriesConfig}
        timeSeriesConfigurations={[chartConfig]}
        parameterModelIndex={parameterModelIndex}
      />
    </div>
  );
}

type TimeSeriesWidgetChartProps = {
  location: ParametersLocation | undefined;
  latlng?: { lat: number; lng: number };
  timeSeriesConfig: TimeSeriesConfig;
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  parameterModelIndex: Record<string, string>;
};

function TimeSeriesWidgetChart({
  location,
  latlng,
  timeSeriesConfig,
  timeSeriesConfigurations,
  parameterModelIndex,
}: TimeSeriesWidgetChartProps) {
  const chartHostRef = useRef<HTMLDivElement>(null);
  const chartHeight = useElementHeight(chartHostRef);
  const { chartData, pending } = useTimeSeriesWidgetChartData({
    station: location,
    latlng,
    timeSeriesConfigs: [timeSeriesConfig],
    timeSeriesConfigurations,
    parameterModelIndex,
  });
  const now = useNow();
  const currentTime = useMemo(() => new TimeRangeDimensionValue(now), [now]);
  const timeRange = useSelectedTimeRange();
  const timeBounds = useMemo(
    () => getSharedChartTimeBounds(chartData, timeRange),
    [chartData, timeRange],
  );

  return (
    <div ref={chartHostRef} className={styles.chart}>
      {pending ? (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size={24} color="inherit" />
        </Stack>
      ) : (
        <Graph
          config={timeSeriesConfig}
          data={chartData}
          currentTime={currentTime}
          timeBounds={timeBounds}
          height={chartHeight}
        />
      )}
    </div>
  );
}

type Props = {
  configuration: TimeSeriesWidgetConfiguration;
};

/**
 * Dashboard renderer for the `time_series` widget: one chart from the singular
 * `parameter_time_series_configuration`, fetched for the selected dashboard
 * location's observed station.
 */
export function TimeSeriesWidget({ configuration }: Props) {
  const { selectedLocation } = useDashboardData();
  return (
    <TimeSeriesWidgetView
      configuration={configuration}
      selectedLocation={selectedLocation}
    />
  );
}

export default TimeSeriesWidget;
